import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { PhoneOutlined, EmailOutlined } from '@mui/icons-material'
import { AlertDto, AlertInstance, AlertSendStatus, Recipient } from '../../../interfaces/IAlert'
import { EventDto } from '../../../interfaces/IEvent'
import { enqueueSnackbar } from 'notistack'
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import DialogCloseButton from '../../../components/Buttons/DialogCloseButton'
import { toDateTime } from '../../../utils/ValueFormatters'
import { SafeHtml } from '../../../components/security/SafeHtml'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'
import { DataGrid } from '@mui/x-data-grid'
import AlertNotifiedCustomersCards from './AlertNotifiedCustomersCards'
import AlertLanguageTabs from '../../../components/data-display/AlertLanguageTabs'

const MetaRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <Grid size={{ xs: 6, sm: 4 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Grid>
    <Grid size={{ xs: 6, sm: 8 }} sx={{ textAlign: 'right' }}>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Grid>
  </>
)

interface ViewAlertProps {
  alertId: string
  eventId: string
  viewEvent: EventDto
  open: boolean
  onClose: () => void
  onPublished?: () => void
}

function ViewAlert({ open, alertId, eventId, viewEvent, onClose, onPublished }: ViewAlertProps) {
  const [viewAlert, setViewAlert] = useState<AlertDto>()
  const [publishLoading, setPublishLoading] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const { context } = useContext(EbAuthenticatedAppContext)
  const theme = useTheme()
  const smFullscreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [alertInstances, setAlertInstances] = useState<AlertInstance[]>()
  const [language, setLanguage] = useState(0)

  const totalRecipientsOnAlert = useMemo<Recipient[] | undefined>(() => alertInstances?.flatMap((i) => i.recipient), [alertInstances])
  const successfulRecipients = useMemo<Recipient[] | undefined>(
    () => totalRecipientsOnAlert?.filter((r) => r.result === 'Sent'),
    [totalRecipientsOnAlert],
  )

  const handleAlertType = (type: string | undefined) => {
    switch (type) {
      case 'Immediate':
        return 'Umiddelbart'
      case 'Scheduled':
        return 'Planlagt'
      default:
        return 'Ukjent'
    }
  }

  const statusChip = (status: AlertSendStatus | undefined) => {
    switch (status) {
      case AlertSendStatus.Draft:
        return <Chip size="small" label="Utkast" />
      case AlertSendStatus.NotProcessed:
        return <Chip size="small" color="info" label="Klar" />
      case AlertSendStatus.Processing:
        return <Chip size="small" color="info" label="Behandles" />
      case AlertSendStatus.Sent:
        return <Chip size="small" color="success" label="Sendt" />
      case AlertSendStatus.SentPartially:
        return <Chip size="small" color="warning" label="Delvis sendt" />
      case AlertSendStatus.FailedBeforeSend:
        return <Chip size="small" color="error" label="Feilet" />
      default:
        return null
    }
  }

  const handleGetAlert = async (alertId: string | undefined) => {
    const response = await context.restApiClient
      .buildRequest(`/api/event/${eventId}/alert/${alertId}`)
      .deserializeResponseToJson<AlertDto>()
      .getAsync()

    if (response.json) {
      return setViewAlert(response.json)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke se varsel {alertId}. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }
  }

  const handlePublishAlert = async () => {
    if (!viewAlert) return

    const hasReceivers =
      (viewAlert.notifiedCustomers?.companyCustomerIds?.length ?? 0) > 0 ||
      (viewAlert.notifiedCustomers?.companyWithSlaCustomerIds?.length ?? 0) > 0 ||
      (viewAlert.notifiedCustomers?.privateCustomerIds?.length ?? 0) > 0 ||
      viewAlert.notifiedCustomers?.allCompanyCustomers ||
      (viewAlert.phoneNumbers?.length ?? 0) > 0 ||
      (viewAlert.emailAddresses?.length ?? 0) > 0

    if (!hasReceivers) {
      setPublishError('Varselet har ingen mottakere. Legg til mottakere før publisering.')
      return
    }

    setPublishError(null)
    setPublishLoading(true)

    const response = await context.restApiClient
      .buildRequest(`/api/event/${eventId}/alert/update-alert`)
      .serializeToJsonContent({ ...viewAlert, status: AlertSendStatus.NotProcessed })
      .putAsync()

    setPublishLoading(false)

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke publisere varsel. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar(<Typography variant="body2">Varsel publisert.</Typography>, { variant: 'success' })
      onPublished?.()
      onClose()
    }
  }

  const handleGetAlertStatus = async (alertId: string | undefined) => {
    const response = await context.restApiClient
      .buildRequest(`/api/event/${eventId}/alert/${alertId}/status-2`)
      .deserializeResponseToJson<AlertInstance[]>()
      .getAsync()

    if (response.json) {
      return setAlertInstances(response.json.map((alertInstance) => alertInstance).flat())
    }

    if (response.error) {
      enqueueSnackbar(
        <Typography variant="body2">Kunne ikke hente status for varsel {alertId}. Prøv igjen eller kontakt systemansvarlig.</Typography>,
        { variant: 'error' },
      )
    }
  }

  useEffect(() => {
    handleGetAlert(alertId)
    handleGetAlertStatus(alertId)
    setLanguage(0)
  }, [alertId])

  return (
    <>
      {viewAlert && (
        <Dialog scroll={'paper'} maxWidth="md" fullWidth fullScreen={smFullscreen} open={open} onClose={onClose}>
          <DialogTitle>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pr: 4 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {viewAlert?.description}
              </Typography>
              {statusChip(viewAlert?.status)}
            </Stack>
            <Typography variant="body2">{viewAlert?.id}</Typography>
            <DialogCloseButton handleClose={onClose} />
          </DialogTitle>
          <DialogContent dividers={true}>
            <Grid container>
              <MetaRow label="Opprettet av">{viewAlert.createdBy.name}</MetaRow>
              <MetaRow label="Opprettet">{toDateTime(new Date(viewAlert.createdOn))}</MetaRow>
              {!(viewAlert.type === 'Immediate' && viewAlert.status === AlertSendStatus.Draft) && (
                <MetaRow label="Sendetidspunkt">{viewAlert.sendOn ? toDateTime(new Date(viewAlert.sendOn)) : ''}</MetaRow>
              )}
              <MetaRow label="Type">{handleAlertType(viewAlert.type)}</MetaRow>
              <MetaRow label="Kanal">{viewAlert.channel ?? ''}</MetaRow>
              {!alertInstances && (
                <Grid size={{ xs: 6, sm: 8 }}>
                  <Stack direction={'row'}>
                    <LoadingIndicator size="xsmall" description="Henter status på varsling..." />
                  </Stack>
                </Grid>
              )}
              {alertInstances && successfulRecipients && totalRecipientsOnAlert && totalRecipientsOnAlert.length > 0 && (
                <MetaRow label="Mottakere varslet">{`${successfulRecipients.length} / ${totalRecipientsOnAlert.length}`}</MetaRow>
              )}
              {alertInstances &&
                alertInstances.length === 0 &&
                (() => {
                  const expectedCount =
                    (viewAlert.notifiedCustomers?.companyCustomerIds?.length ?? 0) +
                    (viewAlert.notifiedCustomers?.companyWithSlaCustomerIds?.length ?? 0) +
                    (viewAlert.notifiedCustomers?.privateCustomerIds?.length ?? 0) +
                    (viewAlert.phoneNumbers?.length ?? 0) +
                    (viewAlert.emailAddresses?.length ?? 0)
                  const allCompany = viewAlert.notifiedCustomers?.allCompanyCustomers
                  const hasAny = expectedCount > 0 || allCompany
                  return (
                    <>
                      <MetaRow label="Forventede mottakere">
                        {allCompany ? 'Alle bedriftskunder' : expectedCount > 0 ? String(expectedCount) : 'Ingen mottakere valgt enda'}
                      </MetaRow>
                      {hasAny && (
                        <Grid size={12} sx={{ mt: 1 }}>
                          <AlertNotifiedCustomersCards viewAlert={viewAlert} viewEvent={viewEvent} />
                        </Grid>
                      )}
                    </>
                  )
                })()}

              {alertInstances &&
                alertInstances.length > 0 &&
                totalRecipientsOnAlert &&
                (() => {
                  const sentCount = totalRecipientsOnAlert.filter((r) => r.result === 'Sent').length
                  const failedCount = totalRecipientsOnAlert.filter((r) => r.result === 'Failed').length
                  const otherCount = totalRecipientsOnAlert.length - sentCount - failedCount
                  const hasExtraStatuses = failedCount > 0 || otherCount > 0
                  return (
                    <Grid size={12} sx={{ mt: 1 }}>
                      {hasExtraStatuses && (
                        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          {failedCount > 0 && <Chip size="small" color="error" label={`Feilet ${failedCount}`} />}
                          {otherCount > 0 && <Chip size="small" label={`Andre ${otherCount}`} />}
                        </Stack>
                      )}
                      <DataGrid
                        showToolbar={true}
                        getRowId={(row) => row.id ?? `${row.displayName}|${row.channelUsed}|${row.lastSendAttempt}|${row.result}`}
                        hideFooterSelectedRowCount
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        rows={totalRecipientsOnAlert}
                        columns={[
                          { field: 'displayName', headerName: 'Navn', minWidth: 200, flex: 1 },
                          {
                            field: 'channelUsed',
                            headerName: 'Kanal',
                            width: 90,
                            renderCell: (params) => {
                              const v = (params.value ?? '') as string
                              const icon = /sms|phone|tlf|telefon/i.test(v) ? (
                                <PhoneOutlined fontSize="small" titleAccess={v} />
                              ) : /email|epost|mail/i.test(v) ? (
                                <EmailOutlined fontSize="small" titleAccess={v} />
                              ) : null
                              return <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{icon ?? (v || '-')}</Box>
                            },
                          },
                          {
                            field: 'result',
                            headerName: 'Resultat',
                            width: 130,
                            renderCell: (params) => {
                              const v = params.value as string
                              if (v === 'Sent') return <Chip size="small" color="success" label="Sendt" />
                              if (v === 'Failed') return <Chip size="small" color="error" label="Feilet" />
                              return <Chip size="small" label={v || '-'} />
                            },
                          },
                          {
                            field: 'lastSendAttempt',
                            headerName: 'Forsøkt',
                            width: 160,
                            renderCell: (params) => (params.value ? toDateTime(new Date(params.value as string)) : ''),
                          },
                        ]}
                        initialState={{
                          pagination: {
                            paginationModel: {
                              pageSize: 10,
                            },
                          },
                          sorting: {
                            sortModel: [
                              {
                                field: 'displayName',
                                sort: 'asc',
                              },
                            ],
                          },
                        }}
                      />
                    </Grid>
                  )
                })()}
            </Grid>
            <Grid container>
              <Grid size={12}>
                <Divider sx={{ mt: 2 }} />
                {viewAlert.englishTemplate && (
                  <Box sx={{ mt: 2 }}>
                    <AlertLanguageTabs value={language} onChange={setLanguage} />
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                  }}
                >
                  <SafeHtml
                    element="span"
                    html={(language === 1 && viewAlert.englishTemplate ? viewAlert.englishTemplate : viewAlert.template).editedText}
                  />
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
            {publishError && (
              <Alert severity="error" onClose={() => setPublishError(null)}>
                {publishError}
              </Alert>
            )}
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }} spacing={1}>
              {viewAlert.status === AlertSendStatus.Draft && (
                <Button onClick={handlePublishAlert} color="success" variant="contained" disabled={publishLoading}>
                  {publishLoading ? 'Publiserer...' : 'Publiser varsel'}
                </Button>
              )}
              <Button onClick={onClose} color="primary" variant="contained">
                Lukk
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default ViewAlert
